import type { ProgramsSearch, SortDirection, SortKey } from './programs-search.schema';

/**
 * Sort dropdown option as presented to the user. Mirror of companies's
 * `SortOption` (see `companies-list/companies-state.ts`) so the recruiter
 * SPA's two consultant listings share a single shape.
 */
export interface SortOption {
  /** Discriminator stored at `search.sort` */
  sort: SortKey;
  /** Discriminator stored at `search.dir` */
  dir: SortDirection;
  /** UI label, pt-BR */
  label: string;
}

/**
 * Four-option sort menu, same wording + ordering used by Empresas. The
 * "no selection" state is the absence of `search.sort` in the URL — not
 * a separate entry here. Each option pins a single direction.
 */
export const SORT_OPTIONS: readonly SortOption[] = [
  { sort: 'createdAt', dir: 'desc', label: 'Data de criação' },
  { sort: 'name', dir: 'asc', label: 'Ordem alfabética' },
  { sort: 'status', dir: 'desc', label: 'Status' },
  { sort: 'updatedAt', dir: 'desc', label: 'Última modificação' },
] as const;

/** Stable id used as both `key` and dropdown discriminator: `${sort}-${dir}`. */
export function sortOptionId(sort: SortKey, dir: SortDirection): string {
  return `${sort}-${dir}`;
}

/** Resolve the currently selected option from URL state, if any. */
export function selectedSortOption(search: ProgramsSearch): SortOption | undefined {
  if (!search.sort) return undefined;
  return SORT_OPTIONS.find((o) => o.sort === search.sort && o.dir === search.dir);
}

/**
 * Apply a sort selection from the dropdown. Resets `page` to 1 — changing
 * order makes the previous page offset meaningless.
 */
export function applySortChange(
  search: ProgramsSearch,
  option: { sort: SortKey; dir: SortDirection }
): ProgramsSearch {
  return { ...search, sort: option.sort, dir: option.dir, page: 1 };
}

/**
 * Clear any active sort. Returns to the server's default order
 * (`createdAt DESC`). `dir` snaps back to the schema default ('desc')
 * so the URL query string normalizes to the same shape as a fresh visit.
 */
export function clearSortChange(search: ProgramsSearch): ProgramsSearch {
  return { ...search, sort: undefined, dir: 'desc', page: 1 };
}
